import { Label } from 'components/ui/label';
import { Button } from 'components/ui/button';
import { RadioGroup, RadioGroupItem } from 'components/ui/radio-group';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';

type KnowledgeRecallModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scoreThreshold: number;
  onScoreThresholdChange: (value: number) => void;
  topK: number;
  onTopKChange: (value: number) => void;
  denseWeight: number;
  onDenseWeightChange: (value: number) => void;
};

export function KnowledgeRecallModal({
  open,
  onOpenChange,
  scoreThreshold,
  onScoreThresholdChange,
  topK,
  onTopKChange,
  denseWeight,
  onDenseWeightChange,
}: KnowledgeRecallModalProps) {
  const semanticPercentage = Math.round(denseWeight * 100);
  const keywordPercentage = Math.round((1 - denseWeight) * 100);
  const searchType = denseWeight >= 0.999 ? 'semantic' : 'mixed';

  const handleSearchTypeChange = (value: string) => {
    if (value === 'semantic') {
      onDenseWeightChange(1);
      return;
    }

    if (denseWeight >= 0.999) {
      onDenseWeightChange(0.8);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Knowledge Recall</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <Label htmlFor="knowledge-relevance">Knowledge Relevance</Label>
              <span className="text-sm font-semibold text-primary">
                {scoreThreshold.toFixed(2)}
              </span>
            </div>
            <input
              id="knowledge-relevance"
              type="range"
              min={0}
              max={0.95}
              step={0.01}
              value={scoreThreshold}
              onChange={(event) => onScoreThresholdChange(Number(event.target.value))}
              className="h-2 w-full cursor-pointer appearance-none rounded-full bg-muted accent-primary"
            />
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <Label htmlFor="knowledge-top-k">Recall Number</Label>
              <span className="text-sm font-semibold text-primary">{topK}</span>
            </div>
            <input
              id="knowledge-top-k"
              type="range"
              min={1}
              max={10}
              step={1}
              value={topK}
              onChange={(event) => onTopKChange(Number(event.target.value))}
              className="h-2 w-full cursor-pointer appearance-none rounded-full bg-muted accent-primary"
            />
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <Label htmlFor="semantic-weight">Search Weight</Label>
              <span className="text-sm font-semibold text-primary">
                Semantic {semanticPercentage}% / Keywords {keywordPercentage}%
              </span>
            </div>

            <RadioGroup
              value={searchType}
              onValueChange={handleSearchTypeChange}
              className="flex gap-6"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="mixed" id="search-type-mixed" />
                <Label htmlFor="search-type-mixed">Mixed Search</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="semantic" id="search-type-semantic" />
                <Label htmlFor="search-type-semantic">Semantic Search</Label>
              </div>
            </RadioGroup>

            {searchType === 'mixed' ? (
              <input
                id="semantic-weight"
                type="range"
                min={0.7}
                max={0.9}
                step={0.01}
                value={denseWeight}
                onChange={(event) => onDenseWeightChange(Number(event.target.value))}
                className="h-2 w-full cursor-pointer appearance-none rounded-full bg-muted accent-primary"
              />
            ) : (
              <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
                Semantic search uses 100% semantic weight and 0% keyword weight.
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button type="button" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
